import React, {
  PropTypes as T,
  Component,
} from 'react';
import radium from 'radium';
import { connect } from 'react-redux';

// Component
import SimulatorPlayer from './SimulatorPlayer.js';

// Icons
import water from '../../static/images/water-icon.png';
import tree from '../../static/images/tree-icon.png';
import dog from '../../static/images/dog-icon.png';
import phone from '../../static/images/phone-icon.png';
import bird from '../../static/images/bird-icon.png';
import plane from '../../static/images/plane-icon.png';
import bus from '../../static/images/bus-icon.png';

const POINT_SIZE = 8;
const BOX_SIZE = 16;
const BOX_OFFSET = BOX_SIZE / 2;
const BOARD_OFFSET_LEFT = 40;
const BOARD_HORIZONTAL_BLOCK_SIZE = POINT_SIZE + 1;
const BOARD_VERTICAL_BLOCK_SIZE = 14;
const RADIUS = 8;
const yAix = ['0', '20', '40', '60', '80', '100', '120'];
const xAix = ['125', '250', '300', '500', '1k', '2k', '4k', '8k'];
const icons = [
  { iconName: 'water', x: 2, y: 3 },
  { iconName: 'tree', x: 5, y: 2 },
  { iconName: 'bird', x: 8, y: 4 },
  { iconName: 'phone', x: 6, y: 6 },
  { iconName: 'dog', x: 3, y: 7 },
  { iconName: 'bus', x: 1, y: 11 },
  { iconName: 'plane', x: 7, y: 13 },
];


const styles = {
  wrap: {
    padding: 24,
    backgroundColor: '#4a4a4a',
    display: 'block',
    height: 'calc(100vh - 150px)',
    '@media (max-width: 1024px)': {
      height: 'auto',
    }
  },
  container: {
    width: 870,
    margin: '0 auto',
  },
  canvas: {
    boxSizing: 'border-box',
    '@meida (max-width: 1024px)': {
      padding: '12px',
    },
  },
  icon: {
    display: 'none',
  },
};

class SimulatorResult extends Component {
  constructor(props) {
    super(props);

    this.drawing = true;

    this.state = {
      leftPoints: [],
      rightPoints: [],
      isDown: false,
      startX: 0,
      startY: 0,
      offsetX: 0,
      offsetY: 0,
      blockW: 0,
      blockH: 0,
      isLeftEar: true,
      isRightEar: true,
    };

    this.resizeListener = () => this.onWindowResize();
  }

  componentDidMount() {
    this.canvas = document.getElementById('canvas');
    this.ctx = this.canvas.getContext('2d');

    window.addEventListener('resize', this.resizeListener);
    this.onWindowResize();
  }

  componentDidUpdate(prevProps, prevState) {
    if (!prevState.leftPoints.length && this.state.leftPoints.length) {
      this.draw();
    }
    if (!prevState.rightPoints.length && this.state.rightPoints.length) {
      this.draw();
    }
  }

  componentWillUnmount() {
    this.drawing = false;
    this.ctx = null;

    window.removeEventListener('resize', this.resizeListener);
  }

  onWindowResize() {
    const windowWidth = document.body.clientWidth > 1200 ? 1200 : document.body.clientWidth;
    const windowHeight = document.body.clientHeight > 800 ? 800 : document.body.clientHeight;
    let height = (windowHeight - 380) - ((windowHeight - 380) % BOARD_VERTICAL_BLOCK_SIZE);

    let width = windowWidth - 398 - ((windowWidth - 398) % BOARD_HORIZONTAL_BLOCK_SIZE);

    if (windowWidth <= 1024) {
      width = windowWidth - 98 - ((windowWidth - 98) % BOARD_HORIZONTAL_BLOCK_SIZE);
      height = (windowHeight - 430) - ((windowHeight - 430) % BOARD_VERTICAL_BLOCK_SIZE);
    }

    const blockW = width / BOARD_HORIZONTAL_BLOCK_SIZE;
    const blockH = height / BOARD_VERTICAL_BLOCK_SIZE;
    const {
      left,
      right,
    } = this.props;

    // Make points
    const leftPoints = Array.apply(null, { length: POINT_SIZE }).map((n, idx) => ({
      x: BOARD_OFFSET_LEFT + blockW * (idx + 1),
      y: ((left[idx] / 10) * blockH) + blockH,
    }));

    const rightPoints = Array.apply(null, { length: POINT_SIZE }).map((n, idx) => ({
      x: BOARD_OFFSET_LEFT + blockW * (idx + 1),
      y: ((right[idx] / 10) * blockH) + blockH,
    }));

    this.setState({
      leftPoints,
      rightPoints,
      blockW,
      blockH,
    });
  }

  drawBoard() {
    const {
      blockW,
      blockH,
    } = this.state;

    const {
      width,
      height,
    } = this.canvas;

    this.ctx.strokeStyle = '#9B9B9B';
    this.ctx.clearRect(0, 0, width, height);
    this.ctx.beginPath();
    this.ctx.lineWidth = 1;

    // horizontal
    const horizontalEnd = blockW * BOARD_HORIZONTAL_BLOCK_SIZE;
    Array.apply(null, { length: BOARD_VERTICAL_BLOCK_SIZE + 1 }).forEach((n, idx) => {
      this.ctx.moveTo(BOARD_OFFSET_LEFT, blockH * idx);
      this.ctx.lineTo(BOARD_OFFSET_LEFT + horizontalEnd, blockH * idx);
      this.ctx.stroke();
    });

    // vertical
    const verticalEnd = blockH * BOARD_VERTICAL_BLOCK_SIZE;
    Array.apply(null, { length: BOARD_HORIZONTAL_BLOCK_SIZE + 1 }).forEach((n, idx) => {
      this.ctx.moveTo(BOARD_OFFSET_LEFT + (blockW * idx), 0);
      this.ctx.lineTo(BOARD_OFFSET_LEFT + (blockW * idx), verticalEnd);
      this.ctx.stroke();
    });

    // y-aix
    this.ctx.fillStyle = 'white';
    this.ctx.lineWidth = 3;
    this.ctx.fillText('dB HL', 35, 10);
    this.ctx.textAlign = 'right';
    yAix.forEach((n, idx) => {
      this.ctx.fillText(n, 20, blockH * idx * 2 + blockH + 5);
    });
    // x-aix
    this.ctx.fillStyle = 'white';
    this.ctx.lineWidth = 3;
    this.ctx.textAlign = 'right';
    xAix.forEach((n, idx) => {
      this.ctx.fillText(n, blockW * idx + blockW + BOARD_OFFSET_LEFT + 8, blockH * BOARD_VERTICAL_BLOCK_SIZE + 20);
    });

    // icons
    icons.forEach((icon) => {
      const image = document.getElementById(icon.iconName);
      const imageSize = blockW * 0.8;
      this.ctx.drawImage(
        image,
        BOARD_OFFSET_LEFT + icon.x * blockW - (imageSize / 2),
        icon.y * blockH - (imageSize / 2),
        imageSize,
        imageSize,
      );
    });

    this.ctx.fillText(
      'Hz',
      blockW * BOARD_HORIZONTAL_BLOCK_SIZE + BOARD_OFFSET_LEFT + 8,
      blockH * BOARD_VERTICAL_BLOCK_SIZE + 20
    );
  }

  draw() {
    const {
      blockW,
      blockH,
      leftPoints,
      rightPoints,
      isLeftEar,
      isRightEar,
    } = this.state;

    if (this.ctx) this.drawBoard();


    if (isLeftEar && this.ctx) {
      // Draw connectors
      this.ctx.beginPath();
      this.ctx.lineWidth = 1;
      this.ctx.strokeStyle = '#5CBDE7';
      this.ctx.fillStyle = 'rgba(92, 189, 231, 0.3)';
      this.ctx.moveTo(BOARD_OFFSET_LEFT + blockW, blockH);

      leftPoints.forEach((point) => {
        this.ctx.lineTo(point.x, point.y);
      });

      this.ctx.lineTo(BOARD_OFFSET_LEFT + blockW * (BOARD_HORIZONTAL_BLOCK_SIZE - 1), blockH);
      this.ctx.lineTo(BOARD_OFFSET_LEFT + blockW, blockH);
      this.ctx.stroke();
      this.ctx.fill();

      // Draw times
      this.ctx.strokeStyle = '#5CBDE7';
      this.ctx.lineWidth = 3;

      leftPoints.forEach((point) => {
        this.ctx.beginPath();
        this.ctx.moveTo(point.x - BOX_OFFSET, point.y - BOX_OFFSET);
        this.ctx.lineTo(point.x + BOX_OFFSET, point.y + BOX_OFFSET);
        this.ctx.stroke();

        this.ctx.beginPath();
        this.ctx.moveTo(point.x + BOX_OFFSET, point.y - BOX_OFFSET);
        this.ctx.lineTo(point.x - BOX_OFFSET, point.y + BOX_OFFSET);
        this.ctx.stroke();
      });
    }

    if (isRightEar && this.ctx) {
      // Draw connectors
      this.ctx.beginPath();
      this.ctx.lineWidth = 1;
      this.ctx.strokeStyle = '#FF0006';
      this.ctx.fillStyle = 'rgba(255, 0, 6, 0.3)';
      this.ctx.moveTo(BOARD_OFFSET_LEFT + blockW, blockH);

      rightPoints.forEach((point) => {
        this.ctx.lineTo(point.x, point.y);
      });

      this.ctx.lineTo(BOARD_OFFSET_LEFT + blockW * (BOARD_HORIZONTAL_BLOCK_SIZE - 1), blockH);
      this.ctx.lineTo(BOARD_OFFSET_LEFT + blockW, blockH);
      this.ctx.stroke();
      this.ctx.fill();

      // Draw times
      this.ctx.strokeStyle = '#FF0006';
      this.ctx.lineWidth = 3;

      rightPoints.forEach((point) => {
        // Create a circular clipping path
        this.ctx.save();
        this.ctx.beginPath();

        this.ctx.strokeStyle = '#9B9B9B';
        this.ctx.arc(point.x, point.y, RADIUS, 0, Math.PI * 2, true);
        this.ctx.clip();

        this.ctx.strokeStyle = '#FF0006';
        this.ctx.arc(point.x, point.y, RADIUS, 0, 2 * Math.PI, false);
        this.ctx.fillStyle = '#FF0006';
        this.ctx.fill();
        this.ctx.stroke();
        this.ctx.restore();
      });
    }

    if (this.drawing) requestAnimationFrame(() => this.draw());
  }

  switch(field, value) {
    this.setState({
      [field]: value,
    });
  }

  render() {
    const windowWidth = document.body.clientWidth > 1200 ? 1200 : document.body.clientWidth;
    const windowHeight = document.body.clientHeight > 800 ? 800 : document.body.clientHeight;
    let height = windowHeight - 350;

    let width = windowWidth - 348;

    if (windowWidth <= 1024) {
      width = windowWidth - 48 - ((windowWidth - 48) % BOARD_HORIZONTAL_BLOCK_SIZE);
      height = windowHeight - 400;
    }


    return (
      <div style={styles.wrap}>
        <div style={styles.container}>
          <canvas
            id="canvas"
            style={styles.canvas}
            width={width}
            height={height}>
          </canvas>
        </div>
        <SimulatorPlayer
          switchFunc={(field, value) => this.switch(field, value)}
          isLeftEar={this.state.isLeftEar}
          isRightEar={this.state.isRightEar} />
        <div style={styles.icon}>
          <img id="water" src={water} role="presentation" />
          <img id="tree" src={tree} role="presentation" />
          <img id="dog" src={dog} role="presentation" />
          <img id="phone" src={phone} role="presentation" />
          <img id="plane" src={plane} role="presentation" />
          <img id="bird" src={bird} role="presentation" />
          <img id="bus" src={bus} role="presentation" />
        </div>
      </div>
    );
  }
}

SimulatorResult.propTypes = {
  left: T.arrayOf(T.number),
  right: T.arrayOf(T.number),
};

export default connect(
  state => ({
    left: state.Simulator.leftResult,
    right: state.Simulator.rightResult,
  }),
)(radium(SimulatorResult));



// WEBPACK FOOTER //
// ./containers/Simulators/SimulatorResult.js